import { useCallback, useEffect, useRef, useState } from "react";
import { AppError } from "../../../lib/appError";
import {
  agentService,
  AGENT_NOT_READY,
  appliedChangesForTurn,
  confirmPendingMessage,
  mergeMessage,
  nextTurn,
  PENDING_USER_PREFIX,
  reconcileMessages,
  runningTurnStub,
  toChatMessage,
  TURN_IN_PROGRESS,
  turnPhase,
  type AgentChatMessage,
  type AgentMessage,
  type AgentTurn,
  type AppliedChange,
} from "../../agent";
import { useAuthSession } from "../../../hooks/useAuthSession";

// Frontend chat state on top of the agent service. Sending saves the user's
// message and asks the runtime to start a turn (202 `{ turn_id }`); from then
// on everything arrives as rows over Realtime — tool rows as they are
// applied, the final reply, and the `agent_turns` row's status. There is no
// polling. Realtime delivery is not guaranteed, so the transcript and the
// latest turn are re-fetched whenever the channel (re)subscribes, when the
// tab becomes visible again, and when a turn ends.
//
// `onApplied` is the reload hook the editor wires up to refresh its own
// site tree after a turn that changed something — the chat never mutates the
// site directly.
export const useAgentChat = (
  siteId: string,
  isSubscribed: boolean,
  onApplied?: (changes: AppliedChange[]) => void | Promise<void>,
) => {
  // App gates the editor on an existing session, but this hook starts its
  // own auth listener, which resolves asynchronously — userId is null for
  // a moment on mount even though the user is signed in. Nothing is
  // judged until authLoading settles, so the not-signed-in error is only
  // ever real.
  const { session, loading: authLoading } = useAuthSession();
  const userId = session?.user.id ?? null;
  const [conversationId, setConversationId] = useState<string | null>(null);
  // The conversation whose first load has finished; until it matches
  // `conversationId` the panel shows its loading state.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [messages, setMessages] = useState<AgentChatMessage[]>([]);
  const [turn, setTurn] = useState<AgentTurn | null>(null);
  const [fetching, setFetching] = useState(false);
  // Between pressing send and the 202 (the turn row is not known yet).
  const [starting, setStarting] = useState(false);
  const [stopRequestedFor, setStopRequestedFor] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState("");

  // Bumped whenever the site or conversation changes under an in-flight
  // action; an action that finds it moved on discards its result.
  const generationRef = useRef(0);
  const pendingCounterRef = useRef(0);
  // Turn ids this tab saw running, and those whose end it has already acted
  // on. A turn that was already over when the chat opened is never "ended".
  const observedRunningRef = useRef(new Set<string>());
  const settledRef = useRef(new Set<string>());
  const onAppliedRef = useRef(onApplied);
  useEffect(() => {
    onAppliedRef.current = onApplied;
  });

  // Resolve the site's active conversation. Whatever the previous site or
  // user left behind is dropped when its effect is cleaned up, and bumping
  // the generation orphans any send or clear still in flight for it.
  useEffect(() => {
    let active = true;
    const resetConversation = () => {
      generationRef.current += 1;
      setConversationId(null);
      setLoadedFor(null);
      setMessages([]);
      setTurn(null);
      setStarting(false);
      setStopRequestedFor(null);
    };
    // Unsubscribed users keep read access to past conversations (nothing
    // sensitive changes), but the panel never touches the database until
    // they subscribe.
    if (authLoading || !isSubscribed) return resetConversation;
    (async () => {
      try {
        setFetching(true);
        if (!userId) throw new Error("You must be signed in.");
        const conversation = await agentService.activeConversation(siteId, userId);
        if (!active) return;
        setError("");
        setConversationId(conversation.id);
      } catch (loadError) {
        if (active)
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Could not open the conversation.",
          );
      } finally {
        if (active) setFetching(false);
      }
    })();
    return () => {
      active = false;
      resetConversation();
    };
  }, [siteId, userId, authLoading, isSubscribed]);

  // Follow the conversation: load it, then keep it current over Realtime.
  useEffect(() => {
    if (!conversationId || !isSubscribed) return;
    let active = true;
    let loaded = false;
    observedRunningRef.current = new Set();
    settledRef.current = new Set();

    // A turn reached a terminal status: the transcript is the source of
    // truth, so reload it, then tell the editor what the turn changed.
    const settle = async (finished: AgentTurn, known?: AgentMessage[]) => {
      try {
        const rows = known ?? await agentService.listMessages(conversationId);
        if (!active) return;
        setMessages((current) =>
          reconcileMessages(current, rows.map(toChatMessage)),
        );
        const changes = appliedChangesForTurn(rows, finished.id);
        if (changes.length === 0) return;
        try {
          await onAppliedRef.current?.(changes);
        } catch {
          // The chat panel still shows the changes; the editor refresh
          // is best-effort and shouldn't override the turn's result.
        }
      } catch {
        // Let a later sync retry this turn's settle.
        settledRef.current.delete(finished.id);
      }
    };

    // `trusted`: the row came over the live channel, so a terminal status is
    // news. From a fetch it is news only if this tab watched the turn run.
    const handleTurn = (
      incoming: AgentTurn,
      trusted: boolean,
      rows?: AgentMessage[],
    ) => {
      setTurn((current) => nextTurn(current, incoming));
      if (incoming.status === "running") {
        observedRunningRef.current.add(incoming.id);
        return;
      }
      if (settledRef.current.has(incoming.id)) return;
      if (!trusted && !observedRunningRef.current.has(incoming.id)) return;
      settledRef.current.add(incoming.id);
      void settle(incoming, rows);
    };

    const sync = async () => {
      try {
        const [rows, latest] = await Promise.all([
          agentService.listMessages(conversationId),
          agentService.latestTurn(conversationId),
        ]);
        if (!active) return;
        setMessages((current) =>
          reconcileMessages(current, rows.map(toChatMessage)),
        );
        if (latest) handleTurn(latest, false, rows);
        loaded = true;
        setLoadedFor(conversationId);
      } catch (syncError) {
        // A failed refresh keeps what is on screen; only a failed first load
        // is worth telling the user about.
        if (!active || loaded) return;
        setError(
          syncError instanceof Error
            ? syncError.message
            : "Could not open the conversation.",
        );
        setLoadedFor(conversationId);
      }
    };

    void sync();
    const unsubscribe = agentService.subscribeToConversation(conversationId, {
      onMessage: (row) => {
        if (active)
          setMessages((current) => mergeMessage(current, toChatMessage(row)));
      },
      onTurn: (row) => {
        if (active) handleTurn(row, true);
      },
      onSubscribed: () => void sync(),
    });
    const onVisibility = () => {
      if (document.visibilityState === "visible") void sync();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      active = false;
      unsubscribe();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [conversationId, isSubscribed]);

  // Loading covers the auth window and the first load; an unsubscribed
  // user settles immediately because nothing is ever fetched for them.
  const loading =
    authLoading ||
    (isSubscribed &&
      (fetching || (conversationId !== null && loadedFor !== conversationId)));

  const phase = turnPhase(turn, starting, stopRequestedFor);
  const working = phase === "working" || phase === "stopping";

  const runTurn = useCallback(
    async (input: string): Promise<void> => {
      const text = input.trim();
      if (!text || working) return;
      const generation = generationRef.current;
      const stale = () => generation !== generationRef.current;
      const pendingId = `${PENDING_USER_PREFIX}${++pendingCounterRef.current}`;
      setStarting(true);
      setError("");
      try {
        if (!isSubscribed)
          throw new AppError(
            "Ducky is part of Hazumu Pro — subscribe to keep chatting.",
            "SUBSCRIPTION_REQUIRED",
          );
        if (!conversationId || !userId) throw new Error(AGENT_NOT_READY);
        setMessages((current) => [
          ...current,
          {
            id: pendingId,
            role: "user",
            content: text,
            changes: [],
            createdAt: new Date().toISOString(),
          },
        ]);
        const saved = await agentService.appendUserMessage(conversationId, text);
        if (stale()) return;
        setMessages((current) =>
          confirmPendingMessage(current, pendingId, toChatMessage(saved)),
        );

        const { turnId } = await agentService.runTurn({
          conversationId,
          message: text,
          // Hand the runtime the row we already wrote. The runtime looks
          // it up by id and skips its own insert; HAZ-48 keeps the
          // "message is persisted before the runtime responds" promise
          // even if the runtime is down.
          userMessageId: saved.id,
        });
        if (stale()) return;
        // Show the turn as running now; the real row replaces this stub as
        // soon as Realtime or a fetch delivers it. If it already arrived
        // (even finished), leave it alone.
        observedRunningRef.current.add(turnId);
        setTurn((current) =>
          current?.id === turnId
            ? current
            : nextTurn(current, runningTurnStub(turnId, conversationId, userId)),
        );
      } catch (turnError) {
        if (stale()) return;
        if (
          turnError instanceof AppError &&
          turnError.code === TURN_IN_PROGRESS
        ) {
          // Another tab (or an earlier send) owns the conversation: say so,
          // and pick up the running turn so Stop and the live rows work here.
          setError(turnError.message);
          if (conversationId) {
            try {
              const latest = await agentService.latestTurn(conversationId);
              if (!stale() && latest) {
                if (latest.status === "running")
                  observedRunningRef.current.add(latest.id);
                setTurn((current) => nextTurn(current, latest));
              }
            } catch {
              // The channel will deliver it; the notice already explains.
            }
          }
          return;
        }
        setError(
          turnError instanceof Error ? turnError.message : AGENT_NOT_READY,
        );
        // Drop only the optimistic user row; a saved one stays.
        setMessages((current) =>
          current.filter((message) => message.id !== pendingId),
        );
      } finally {
        if (!stale()) setStarting(false);
      }
    },
    [conversationId, userId, working, isSubscribed],
  );

  // Ask the worker to stop. The turn is still running until its status
  // changes, so the panel shows "stopping" until then.
  const stop = useCallback(async () => {
    if (!turn || turn.status !== "running") return;
    if (turn.cancel_requested || stopRequestedFor === turn.id) return;
    const generation = generationRef.current;
    setStopRequestedFor(turn.id);
    try {
      await agentService.cancelTurn(turn.id);
    } catch (stopError) {
      if (generation !== generationRef.current) return;
      setStopRequestedFor(null);
      setError(
        stopError instanceof Error ? stopError.message : "Could not stop Ducky.",
      );
    }
  }, [turn, stopRequestedFor]);

  // Clear the panel by moving to a new conversation. History is
  // append-only, so nothing is deleted — the previous conversation ages out
  // of retention, and `activeConversation` serves the new one from now on.
  const clear = useCallback(async () => {
    if (!isSubscribed || !userId || clearing || working) return;
    const generation = ++generationRef.current; // orphan any in-flight load
    setClearing(true);
    setError("");
    try {
      const conversation = await agentService.startConversation(siteId, userId);
      if (generation !== generationRef.current) return;
      setConversationId(conversation.id);
      setLoadedFor(conversation.id);
      setMessages([]);
      setTurn(null);
      setStopRequestedFor(null);
    } catch (clearError) {
      if (generation === generationRef.current) {
        setError(
          clearError instanceof Error
            ? clearError.message
            : "Could not clear the conversation.",
        );
      }
    } finally {
      // Concurrent clears are impossible (the control disables while
      // `clearing`), so an unconditional reset cannot clobber a newer one
      // and recovers if the site changed mid-clear.
      setClearing(false);
    }
  }, [siteId, userId, isSubscribed, clearing, working]);

  return {
    messages,
    loading,
    working,
    phase,
    clearing,
    error,
    runTurn,
    stop,
    clear,
  };
};
